import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import { normalizeTizenStreamUrl } from 'utils/device/tizenPlatform';
import { getPlayerState, updatePlayerState } from './channelPlayerState';
import {
  logHttp,
  warnHttp,
  errorHttp,
  logNativePlayerState,
  shortUrl,
} from './httpStreamLog';
import { isOnScreenLogEnabled } from 'utils/device/tizenLog';

var HTTP_FALLBACK_WAIT_MS = 1200;
var HTTP_WATCH_MS = 5000;
var HTTP_STALL_MS = 10000;
var HTTP_RECOVER_GAP_MS = 8000;
var STREAM_READY_WAIT_MS = 8000;
/** Hard ceiling for player.play() so dead UDP/live never freezes the UI */
export var PLAY_ATTEMPT_TIMEOUT_MS = 12000;
export var RESET_TIMEOUT_MS = 2000;
var httpWatchTimer = null;
var watchLastHttpMs = 0;
var watchLastAdvanceAt = 0;
var httpRecovering = false;
var lastRecoverAt = 0;

export function isHttpChannelUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }
  return /^https?:\/\//i.test(url.trim());
}

export function hasChannelApiKey(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }
  return /[?&]apikey=/i.test(url);
}

export function appendChannelApiKey(url) {
  if (!url) {
    return url;
  }
  var license = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY);
  var apiKey = license && license.api_key;
  if (!apiKey) {
    return url;
  }
  var separator = url.includes('?') ? '&' : '?';
  return url + separator + 'apikey=' + apiKey;
}

/** HTTP(S): apikey URL first, then bare URL. UDP/other: single URL. */
export function getChannelStreamAttemptUrls(url) {
  if (!url) {
    return [url];
  }

  var normalized = normalizeTizenStreamUrl(url);
  if (!isHttpChannelUrl(normalized) || hasChannelApiKey(normalized)) {
    return [normalized];
  }

  var authedUrl = appendChannelApiKey(normalized);
  if (!authedUrl || authedUrl === normalized) {
    return [normalized];
  }

  return [authedUrl, normalized];
}

function delay(ms) {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

/**
 * Reject if promise does not settle in time — used so dead multicast/live cannot hang the app.
 */
export function withTimeout(promise, ms, label) {
  var timeoutMs = ms == null ? PLAY_ATTEMPT_TIMEOUT_MS : ms;
  var message = label || 'operation timeout';
  return new Promise(function (resolve, reject) {
    var settled = false;
    var timer = setTimeout(function () {
      if (settled) return;
      settled = true;
      reject(new Error(message + ' after ' + timeoutMs + 'ms'));
    }, timeoutMs);

    Promise.resolve(promise).then(
      function (value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      function (err) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function resetWatchStallState() {
  watchLastHttpMs = 0;
  watchLastAdvanceAt = Date.now();
  httpRecovering = false;
}

function stopHttpWatch() {
  if (httpWatchTimer != null) {
    clearInterval(httpWatchTimer);
    httpWatchTimer = null;
    logHttp('watch stopped');
  }
  resetWatchStallState();
}

function recoverHttpStream(player, reason) {
  var state = getPlayerState();
  if (!isHttpChannelUrl(state.url) || !state.url) {
    return;
  }
  if (httpRecovering) {
    return;
  }
  if (Date.now() - lastRecoverAt < HTTP_RECOVER_GAP_MS) {
    logHttp('recover skipped (cooldown) ' + reason);
    return;
  }

  httpRecovering = true;
  lastRecoverAt = Date.now();
  warnHttp('recover: ' + reason);
  logNativePlayerState(player, 'before-recover');

  var url = state.url;
  var isFullScreen = state.displayFullScreen;
  updatePlayerState({ httpPlayTimeMs: 0, streamReady: false });

  resetPlayer(player)
    .then(function () {
      return player.play(url, isFullScreen);
    })
    .then(function () {
      logHttp('recover ok');
      watchLastHttpMs = 0;
      watchLastAdvanceAt = Date.now();
      logNativePlayerState(player, 'after-recover');
    })
    .catch(function (err) {
      errorHttp('recover failed: ' + (err.message || err));
    })
    .finally(function () {
      httpRecovering = false;
    });
}

function checkHttpStall(player) {
  var state = getPlayerState();
  if (!isHttpChannelUrl(state.url)) {
    return;
  }

  if (isOnScreenLogEnabled) {
    logNativePlayerState(player, 'watch');
  }

  var httpMs = state.httpPlayTimeMs || 0;
  if (httpMs > watchLastHttpMs) {
    watchLastHttpMs = httpMs;
    watchLastAdvanceAt = Date.now();
    return;
  }

  var avState = getNativeAvplayState(player);
  if (
    httpMs > 0 &&
    (avState === 'PLAYING' || avState === 'PAUSED') &&
    Date.now() - watchLastAdvanceAt >= HTTP_STALL_MS
  ) {
    recoverHttpStream(player, 'stall httpMs=' + httpMs + ' av=' + avState);
  }
}

function startHttpWatch(player) {
  stopHttpWatch();
  logHttp('watch ' + HTTP_WATCH_MS + 'ms stall=' + HTTP_STALL_MS + 'ms');
  httpWatchTimer = setInterval(function () {
    checkHttpStall(player);
  }, HTTP_WATCH_MS);
}

async function resetPlayer(player) {
  logHttp('player stop (between attempts)');
  if (typeof player.stop === 'function') {
    try {
      await player.stop();
    } catch (e) {
      warnHttp('player stop error: ' + (e.message || e));
    }
    await delay(150);
  }
}

function getNativeAvplayState(player) {
  if (typeof player.getPlayerState !== 'function') {
    return null;
  }
  var avplay = player.getPlayerState().gAVplayObj;
  if (!avplay || typeof avplay.getState !== 'function') {
    return null;
  }
  try {
    return avplay.getState();
  } catch (e) {
    return null;
  }
}

function isHttpStreamStarting(player) {
  var state = player.getPlayerState();
  if (state.httpPlayTimeMs > 0) {
    return true;
  }

  var avplay = state.gAVplayObj;
  if (avplay && typeof avplay.getState === 'function') {
    var avState = avplay.getState();
    if (avState === 'PLAYING' || avState === 'PAUSED') {
      return true;
    }
    if (typeof avplay.getCurrentPlayTime === 'function') {
      try {
        if (avplay.getCurrentPlayTime() > 0) {
          return true;
        }
      } catch (e) {
        // ignore
      }
    }
  }

  var video = document.getElementById('default-video-player');
  if (video && video.src && !video.error) {
    return !video.paused && video.readyState >= 2;
  }

  return false;
}

async function waitUntilPlaying(player, maxWaitMs) {
  var elapsed = 0;
  while (elapsed < maxWaitMs) {
    if (isHttpStreamStarting(player)) {
      logHttp('playback detected at ' + elapsed + 'ms');
      logNativePlayerState(player, 'started');
      return true;
    }
    await delay(200);
    elapsed += 200;
  }
  var ok = isHttpStreamStarting(player);
  warnHttp('wait ' + maxWaitMs + 'ms — playing=' + ok);
  logNativePlayerState(player, 'wait-done');
  return ok;
}

/**
 * Mark channel stream ready for loading UI (buffering complete / first frame / playtime).
 * Ignores stale callbacks from a superseded playRequestId.
 */
export function markChannelStreamReady(myRequestId) {
  var state = getPlayerState();
  if (myRequestId != null && myRequestId !== state.playRequestId) {
    return;
  }
  if (state.streamReady) {
    return;
  }
  updatePlayerState({ streamReady: true });
}

/**
 * True when video is actually buffering-complete / painting — not merely after play() resolves.
 * AVPlay PLAYING alone is too early (native play() returns before first frame).
 */
export function isChannelStreamReady(player) {
  var state = player.getPlayerState();
  if (state.streamReady) {
    return true;
  }
  if (state.httpPlayTimeMs > 0) {
    return true;
  }

  var avplay = state.gAVplayObj;
  if (avplay && typeof avplay.getCurrentPlayTime === 'function') {
    try {
      if (avplay.getCurrentPlayTime() > 0) {
        return true;
      }
    } catch (e) {
      // ignore
    }
  }

  var video = document.getElementById('default-video-player');
  if (video && video.src && !video.error) {
    return !video.paused && video.readyState >= 3;
  }

  return false;
}

/**
 * Keep channel loading overlay until stream is ready (or timeout).
 * @param {function(): boolean} [shouldContinue] - return false to abort early (channel zap)
 */
export async function waitUntilStreamReady(player, maxWaitMs, shouldContinue) {
  var waitMs = maxWaitMs == null ? STREAM_READY_WAIT_MS : maxWaitMs;
  var elapsed = 0;
  while (elapsed < waitMs) {
    if (typeof shouldContinue === 'function' && !shouldContinue()) {
      logHttp('stream ready wait aborted');
      return false;
    }
    if (isChannelStreamReady(player)) {
      logHttp('stream ready at ' + elapsed + 'ms');
      logNativePlayerState(player, 'stream-ready');
      return true;
    }
    await delay(200);
    elapsed += 200;
  }
  if (typeof shouldContinue === 'function' && !shouldContinue()) {
    return false;
  }
  var ok = isChannelStreamReady(player);
  warnHttp('stream ready wait ' + waitMs + 'ms — ready=' + ok);
  logNativePlayerState(player, 'stream-ready-timeout');
  return ok;
}

/**
 * After "Signal unavailable", keep watching — clear the message if stream starts later.
 * Returns a cancel function.
 */
export function watchStreamReadyAfterTimeout(player, onReady, maxWaitMs) {
  var waitMs = maxWaitMs == null ? 60000 : maxWaitMs;
  var elapsed = 0;
  var intervalMs = 500;
  var timer = setInterval(function () {
    elapsed += intervalMs;
    if (isChannelStreamReady(player)) {
      clearInterval(timer);
      logHttp('stream ready after unavailable at ' + elapsed + 'ms');
      if (typeof onReady === 'function') {
        onReady();
      }
      return;
    }
    if (elapsed >= waitMs) {
      clearInterval(timer);
    }
  }, intervalMs);

  return function cancel() {
    clearInterval(timer);
  };
}

var htmlEndedHandler = null;

function clearHtmlLoop() {
  if (htmlEndedHandler) {
    var video = document.getElementById('default-video-player');
    if (video) {
      video.removeEventListener('ended', htmlEndedHandler);
      video.loop = false;
    }
    htmlEndedHandler = null;
  }
}

function setHtmlLoop(url, player) {
  clearHtmlLoop();
  var video = document.getElementById('default-video-player');
  if (!video || !isHttpChannelUrl(url)) {
    return;
  }

  logHttp('html loop on');
  video.loop = true;
  htmlEndedHandler = function () {
    var s = getPlayerState();
    if (!s.httpLoopUrl || s.url !== s.httpLoopUrl) {
      return;
    }
    logHttp('html ended — full restart');
    restartHttpStream(player, s.httpLoopUrl, s.displayFullScreen);
  };
  video.addEventListener('ended', htmlEndedHandler);
}

function restartHttpStream(player, url, isFullScreen) {
  logHttp('full restart ' + shortUrl(url));
  logNativePlayerState(player, 'before-restart');
  updatePlayerState({ url: url, httpPlayTimeMs: 0 });
  resetPlayer(player).then(function () {
    return player.play(url, isFullScreen);
  }).then(function () {
    logHttp('full restart play() ok');
    logNativePlayerState(player, 'after-restart');
  }).catch(function (err) {
    errorHttp('full restart failed: ' + (err.message || err));
  });
}

function tryNativeHttpResume(player) {
  var avState = getNativeAvplayState(player);
  logHttp('stream-completed av=' + (avState || 'n/a'));
  logNativePlayerState(player, 'completed');

  var avplay = player.getPlayerState().gAVplayObj;
  if (!avplay || typeof avplay.play !== 'function') {
    warnHttp('resume skip — no avplay');
    return false;
  }
  if (avState === 'PLAYING' || avState === 'PAUSED') {
    logHttp('resume skip — still active');
    return true;
  }
  if (avState === 'IDLE' || avState === 'NONE' || avState === 'READY') {
    try {
      avplay.play();
      logHttp('native avplay.play() called');
      return true;
    } catch (e) {
      errorHttp('native resume failed: ' + (e.message || e));
    }
  }
  warnHttp('resume skip — state ' + avState);
  return false;
}

export function createHttpStreamCompletedHandler(player, myRequestId) {
  return function onstreamcompleted() {
    var state = getPlayerState();
    if (myRequestId !== state.playRequestId) {
      logHttp('completed ignored — stale req ' + myRequestId);
      return;
    }
    if (!isHttpChannelUrl(state.url)) {
      return;
    }

    var video = document.getElementById('default-video-player');
    if (video) {
      logHttp('completed — html handles loop');
      return;
    }

    tryNativeHttpResume(player);
  };
}

export function createHttpStreamErrorHandler(player, myRequestId) {
  return function onerror(eventType) {
    var state = getPlayerState();
    if (myRequestId !== state.playRequestId || !isHttpChannelUrl(state.url)) {
      return;
    }
    errorHttp('player onerror: ' + eventType);
    logNativePlayerState(player, 'onerror');
    recoverHttpStream(player, 'onerror:' + eventType);
  };
}

export function clearHttpChannelLoop(updateUrl) {
  stopHttpWatch();
  clearHtmlLoop();
  if (typeof updateUrl === 'function') {
    updateUrl({ httpLoopEnabled: false, httpLoopUrl: null });
  }
}

function enableHttpLoop(url, updateUrl, player) {
  if (!isHttpChannelUrl(url)) {
    clearHttpChannelLoop(updateUrl);
    return;
  }

  var video = document.getElementById('default-video-player');
  if (video) {
    if (typeof updateUrl === 'function') {
      updateUrl({ httpLoopEnabled: true, httpLoopUrl: url });
    }
    setHtmlLoop(url, player);
    logHttp('loop mode=html');
    return;
  }

  clearHttpChannelLoop(updateUrl);
  logHttp('loop mode=native (stall watch + resume on completed)');
  startHttpWatch(player);
}

/**
 * HTTP(S): apikey first, bare URL if not playing after a short wait (or play throws).
 * UDP/multicast: pass through unchanged (normalize udp://@ only).
 */
export async function playChannelStream(player, url, isFullScreen, updateUrl) {
  var normalized = normalizeTizenStreamUrl(url);

  if (!isHttpChannelUrl(normalized)) {
    logHttp('non-http passthrough ' + shortUrl(normalized));
    clearHttpChannelLoop(updateUrl);
    if (typeof updateUrl === 'function') {
      updateUrl({ url: normalized, httpPlayTimeMs: 0, streamReady: false });
    }
    return player.play(normalized, isFullScreen);
  }

  clearHttpChannelLoop(updateUrl);
  var attempts = getChannelStreamAttemptUrls(url);
  logHttp('play attempts=' + attempts.length + ' fs=' + isFullScreen);
  var lastError = null;

  for (var i = 0; i < attempts.length; i++) {
    var attemptUrl = attempts[i];
    logHttp('try #' + (i + 1) + ' ' + shortUrl(attemptUrl));

    if (typeof updateUrl === 'function') {
      updateUrl({ url: attemptUrl, httpPlayTimeMs: 0, streamReady: false });
    }

    try {
      await player.play(attemptUrl, isFullScreen);
      logHttp('play() resolved #' + (i + 1));
      logNativePlayerState(player, 'after-play');

      var tryBareNext =
        isHttpChannelUrl(attemptUrl) && hasChannelApiKey(attemptUrl) && i < attempts.length - 1;
      if (tryBareNext) {
        var playing = await waitUntilPlaying(player, HTTP_FALLBACK_WAIT_MS);
        if (!playing) {
          warnHttp('apikey unavailable — fallback bare');
          await resetPlayer(player);
          continue;
        }
      }

      enableHttpLoop(attemptUrl, updateUrl, player);
      logHttp('success #' + (i + 1));
      return;
    } catch (err) {
      lastError = err;
      errorHttp('play() failed #' + (i + 1) + ': ' + (err.message || err));
      logNativePlayerState(player, 'play-fail');
      if (i < attempts.length - 1) {
        warnHttp('fallback bare');
        await resetPlayer(player);
        continue;
      }
      throw err;
    }
  }

  if (lastError) {
    throw lastError;
  }
}
